import React, { useState, useEffect } from 'react';
import { encrypt, decrypt } from '../utils/encryption';

const EncryptionTester = ({ defaultPassword }) => {
  const [text, setText] = useState('');
  const [password, setPassword] = useState(defaultPassword || '');
  const [encryptedText, setEncryptedText] = useState('');
  const [decryptedText, setDecryptedText] = useState('');
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [error, setError] = useState('');

  // Update password when prop changes
  useEffect(() => {
    if (defaultPassword && !password) {
      setPassword(defaultPassword);
    }
  }, [defaultPassword, password]);

  const handleEncrypt = async () => {
    if (!text || !password) {
      setError('Please enter both text and password');
      return;
    }

    setError('');
    setIsEncrypting(true);
    
    try {
      const result = await encrypt(text, password);
      setEncryptedText(result);
    } catch (err) {
      setError(`Encryption error: ${err.message}`);
    } finally {
      setIsEncrypting(false);
    }
  };

  const handleDecrypt = async () => {
    if (!encryptedText || !password) {
      setError('Please enter both encrypted text and password');
      return;
    }

    setError('');
    setIsDecrypting(true);
    
    try {
      const result = await decrypt(encryptedText, password);
      setDecryptedText(result);
    } catch (err) {
      setError(`Decryption error: ${err.message}`);
    } finally {
      setIsDecrypting(false);
    }
  };

  return (
    <div className="p-4 max-w-2xl mx-auto bg-white shadow-md rounded-lg">
      <h2 className="text-xl font-bold mb-4">Encryption Tester</h2>
      
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Text to Encrypt
        </label>
        <textarea
          className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows="3"
          placeholder="Enter text to encrypt"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Password
        </label>
        <input
          type="password"
          className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter encryption password"
        />
      </div>

      <div className="flex space-x-4 mb-6">
        <button
          onClick={handleEncrypt}
          disabled={isEncrypting}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isEncrypting ? 'Encrypting...' : 'Encrypt'}
        </button>
        
        <button
          onClick={handleDecrypt}
          disabled={isDecrypting}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
        >
          {isDecrypting ? 'Decrypting...' : 'Decrypt'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Encrypted Result
        </label>
        <textarea
          className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={encryptedText}
          onChange={(e) => setEncryptedText(e.target.value)}
          rows="3"
          placeholder="Encrypted text will appear here"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Decrypted Result
        </label>
        <textarea
          className="w-full p-2 border rounded bg-gray-50"
          value={decryptedText}
          readOnly
          rows="3"
          placeholder="Decrypted text will appear here"
        />
      </div>
    </div>
  );
};

export default EncryptionTester;